import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import FORMS from "../api/services/FORMS";
import FormBuilder from "../containers/_forms/formBuilder";
import FormPreviewModal from "../containers/_forms/formPreviewModal";
import SubmissionsModal from "../containers/_forms/submissionsModal";
import FORM_TEMPLATES from "../containers/_forms/formTemplates";
import styles from "../styles/_screens/forms.module.scss";

const Icon = ({ name, size = 18 }) => {
  const paths = {
    form: <><path d="M6 3h9l3 3v15H6z"/><path d="M15 3v4h4M9 11h6M9 15h6"/></>,
    survey: <><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 3h6v3H9zM9 11h6M9 15h4"/></>,
    send: <><path d="m21 3-7 18-4-7-7-4zM10 14 21 3"/></>,
    plus: <><path d="M12 5v14M5 12h14"/></>,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></>,
    edit: <><path d="m4 20 4.2-1 10.6-10.6-3.2-3.2L5 15.8zM14.5 6.3l3.2 3.2"/></>,
    trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6"/></>,
    users: <><circle cx="9" cy="8" r="3"/><path d="M3 20c0-4 2.5-6 6-6s6 2 6 6M16 5.5a3 3 0 0 1 0 5.5M17 14c2.5.5 4 2.4 4 5"/></>,
    search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
    close: <><path d="m6 6 12 12M18 6 6 18"/></>,
    copy: <><rect x="8" y="8" width="11" height="11" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
};

const TABS = [
  { key: "declaration", label: "טפסים", icon: "form" },
  { key: "questionnaire", label: "שאלונים", icon: "survey" },
  { key: "links", label: "מעקב שליחות", icon: "send" },
];

const TEMPLATE_ICON = { confidentiality: "01", health: "02", liability: "03", consent: "04", intake: "05" };

const Forms = () => {
  const [tab, setTab] = useState("declaration");
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [submissionsForm, setSubmissionsForm] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [allLinks, setAllLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkTypeFilter, setLinkTypeFilter] = useState("all");
  const [linkStatusFilter, setLinkStatusFilter] = useState("all");

  const fetchAllLinks = useCallback(async () => {
    setLinksLoading(true);
    try { const res = await FORMS.allLinks(); setAllLinks(res?.data || []); }
    catch { setAllLinks([]); }
    finally { setLinksLoading(false); }
  }, []);

  const fetchForms = useCallback(async () => {
    if (tab === "links") return;
    setLoading(true);
    try { const res = await FORMS.list(tab); setForms(res?.data || []); }
    catch { toast.error("לא הצלחנו לטעון את הטפסים"); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchAllLinks(); }, [fetchAllLinks]);
  useEffect(() => { fetchForms(); }, [fetchForms]);

  const filteredLinks = useMemo(() => allLinks.filter((link) => {
    if (linkTypeFilter !== "all" && link.formType !== linkTypeFilter) return false;
    if (linkStatusFilter !== "all" && link.status !== linkStatusFilter) return false;
    const query = linkSearch.trim().toLowerCase();
    return !query || (link.clientName || "").toLowerCase().includes(query) || (link.clientPhone || "").includes(query) || (link.formTitle || "").toLowerCase().includes(query);
  }), [allLinks, linkSearch, linkStatusFilter, linkTypeFilter]);

  const stats = useMemo(() => ({
    forms: forms.length,
    submissions: forms.reduce((sum, form) => sum + (form.submissionsCount || 0), 0),
    active: forms.filter((form) => form.isActive).length,
  }), [forms]);

  const handleBulkSend = async (audience) => {
    setSendLoading(true);
    try {
      const res = await FORMS.bulkSend(sendModal._id, audience);
      const data = res?.data?.data || res?.data || {};
      data.sent === 0 && data.requested === 0 ? toast.info("אין לקוחות מתאימים לשליחה") : toast.success(data.message || `נשלח ל-${data.sent} לקוחות`);
      setSendModal(null);
      fetchAllLinks();
    } catch (error) { toast.error(error?.response?.data?.message || "שגיאה בשליחה"); }
    finally { setSendLoading(false); }
  };

  const handleToggle = async (form) => {
    try {
      await FORMS.toggle(form._id);
      setForms((current) => current.map((item) => item._id === form._id ? { ...item, isActive: !item.isActive } : item));
    } catch { toast.error("שגיאה בשינוי הסטטוס"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("למחוק את הטופס? הפעולה תמחק גם את כל ההגשות.")) return;
    try { await FORMS.remove(id); setForms((current) => current.filter((form) => form._id !== id)); toast.success("הטופס נמחק"); }
    catch { toast.error("שגיאה במחיקה"); }
  };

  const openBuilder = (form = null) => { setEditForm(form); setBuilderOpen(true); };
  const onSaved = (saved) => {
    setBuilderOpen(false); setEditForm(null);
    setForms((current) => current.some((form) => form._id === saved._id) ? current.map((form) => form._id === saved._id ? saved : form) : [saved, ...current]);
    toast.success("נשמר בהצלחה");
  };
  const fmt = (date) => date ? new Date(date).toLocaleDateString("he-IL") : "-";
  const tabTemplates = FORM_TEMPLATES.filter((template) => template.type === tab);

  return (
    <Layout>
      <main className={styles.container} dir="rtl">
        <div className={styles.wrapper}>
          <section className={styles.hero}>
            <div className={styles.heroCopy}>
              <span className={styles.eyebrow}>מסמכים דיגיטליים</span>
              <h1>טפסים ושאלונים</h1>
              <p>יוצרים, שולחים ועוקבים אחר מסמכי הלקוחות במקום אחד - עם חתימה דיגיטלית ו-PDF ממותג.</p>
            </div>
            {tab !== "links" && <button className={styles.primaryBtn} onClick={() => openBuilder()}><Icon name="plus" />{tab === "declaration" ? "טופס חדש" : "שאלון חדש"}</button>}
          </section>

          <section className={styles.summary} aria-label="סיכום פעילות">
            <div><span>מסמכים</span><strong>{stats.forms}</strong></div>
            <div><span>הגשות</span><strong>{stats.submissions}</strong></div>
            <div><span>פעילים</span><strong>{stats.active}</strong></div>
          </section>

          <nav className={styles.tabs} aria-label="סוגי מסמכים">
            {TABS.map((item) => <button key={item.key} className={tab === item.key ? styles.tabActive : ""} onClick={() => setTab(item.key)}><Icon name={item.icon} />{item.label}</button>)}
          </nav>

          {tab !== "links" ? (
            <>
              <section className={styles.section}>
                <div className={styles.sectionHeading}><div><span>התחלה מהירה</span><h2>תבניות מקצועיות מוכנות</h2></div><p>בחרו תבנית, התאימו את התוכן ושלחו ללקוח.</p></div>
                <div className={styles.templates}>
                  {tabTemplates.map((template) => <article className={styles.templateCard} key={template._templateId}>
                    <div className={styles.templateTop}><span className={styles.templateIcon}>{TEMPLATE_ICON[template._templateId] || "01"}</span><span className={styles.builtIn}>מובנה</span></div>
                    <h3>{template.title}</h3><p>{template.description}</p>
                    <div className={styles.templateActions}><button onClick={() => openBuilder(template)}>יצירה מהתבנית</button><button className={styles.iconBtn} onClick={() => setPreviewForm(template)} aria-label="תצוגה מקדימה"><Icon name="eye" /></button></div>
                  </article>)}
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionHeading}><div><span>הספרייה שלי</span><h2>{tab === "declaration" ? "הטפסים שלי" : "השאלונים שלי"}</h2></div></div>
                {loading ? <div className={styles.loaderWrap}><Loader /></div> : forms.length === 0 ? <div className={styles.emptyState}><div className={styles.emptyIcon}><Icon name={tab === "declaration" ? "form" : "survey"} size={28} /></div><h3>עדיין אין כאן מסמכים</h3><p>אפשר להתחיל מתבנית מוכנה או ליצור מסמך חדש מאפס.</p><button onClick={() => openBuilder()}><Icon name="plus" />יצירת {tab === "declaration" ? "טופס" : "שאלון"}</button></div> : (
                  <div className={styles.tableCard}><table><thead><tr><th>מסמך</th><th>נוצר</th><th>הגשות</th><th>חתימה</th><th>סטטוס</th><th>פעולות</th></tr></thead><tbody>{forms.map((form) => <tr key={form._id} className={form.hasAlerts ? styles.alertRow : ""}>
                    <td data-label="מסמך"><div className={styles.documentCell}><span className={styles.documentIcon}><Icon name={form.type === "questionnaire" ? "survey" : "form"} /></span><div><strong>{form.title}</strong>{form.hasAlerts && <small>{form.alertsCount} תשובות דורשות בדיקה</small>}</div></div></td>
                    <td data-label="נוצר">{fmt(form.createdAt)}</td><td data-label="הגשות"><button className={styles.submissionCount} onClick={() => setSubmissionsForm(form)}>{form.submissionsCount || 0}</button></td>
                    <td data-label="חתימה"><span className={form.hasSignature ? styles.signatureOn : styles.muted}>{form.hasSignature ? "נדרשת" : "ללא"}</span></td>
                    <td data-label="סטטוס"><button className={`${styles.statusToggle} ${form.isActive ? styles.statusActive : ""}`} onClick={() => handleToggle(form)}><i />{form.isActive ? "פעיל" : "כבוי"}</button></td>
                    <td data-label="פעולות"><div className={styles.actions}><button onClick={() => setPreviewForm(form)} title="תצוגה"><Icon name="eye" /></button><button onClick={() => setSubmissionsForm(form)} title="הגשות"><Icon name="users" /></button><button onClick={() => openBuilder(form)} title="עריכה"><Icon name="edit" /></button><button onClick={() => setSendModal(form)} title="שליחה"><Icon name="send" /></button><button className={styles.dangerAction} onClick={() => handleDelete(form._id)} title="מחיקה"><Icon name="trash" /></button></div></td>
                  </tr>)}</tbody></table></div>
                )}
              </section>
            </>
          ) : (
            <section className={styles.section}>
              <div className={styles.sectionHeading}><div><span>מעקב בזמן אמת</span><h2>סטטוס שליחות</h2></div><p>מי קיבל, מי פתח ומי כבר השלים את הטופס.</p></div>
              <div className={styles.filters}><label><Icon name="search" /><input value={linkSearch} onChange={(event) => setLinkSearch(event.target.value)} placeholder="חיפוש לפי לקוח, טלפון או טופס" /></label><select value={linkTypeFilter} onChange={(event) => setLinkTypeFilter(event.target.value)}><option value="all">כל סוגי המסמכים</option><option value="declaration">טפסים</option><option value="questionnaire">שאלונים</option></select><select value={linkStatusFilter} onChange={(event) => setLinkStatusFilter(event.target.value)}><option value="all">כל הסטטוסים</option><option value="submitted">הושלם</option><option value="opened">נפתח</option><option value="pending">ממתין</option></select></div>
              {linksLoading ? <div className={styles.loaderWrap}><Loader /></div> : filteredLinks.length === 0 ? <div className={styles.emptyState}><div className={styles.emptyIcon}><Icon name="send" size={28} /></div><h3>{allLinks.length ? "אין תוצאות מתאימות" : "עדיין לא נשלחו טפסים"}</h3><p>{allLinks.length ? "נסו לשנות את החיפוש או הסינון." : "שלחו טופס ללקוח והסטטוס יוצג כאן."}</p></div> : <div className={styles.tableCard}><table><thead><tr><th>לקוח</th><th>טלפון</th><th>מסמך</th><th>סוג</th><th>סטטוס</th><th>נשלח</th></tr></thead><tbody>{filteredLinks.map((link, index) => <tr key={link._id || index}><td data-label="לקוח"><strong>{link.clientName || "-"}</strong></td><td data-label="טלפון" className={styles.phone}>{link.clientPhone || "-"}</td><td data-label="מסמך">{link.formTitle || "-"}</td><td data-label="סוג">{link.formType === "questionnaire" ? "שאלון" : "טופס"}</td><td data-label="סטטוס"><span className={`${styles.linkStatus} ${styles[link.status] || ""}`}>{link.status === "submitted" ? "הושלם" : link.status === "opened" ? "נפתח" : "ממתין"}</span></td><td data-label="נשלח">{fmt(link.sentAt)}</td></tr>)}</tbody></table></div>}
            </section>
          )}
        </div>
      </main>

      {builderOpen && <FormBuilder type={editForm?.type || tab} initialData={editForm} onClose={() => { setBuilderOpen(false); setEditForm(null); }} onSaved={onSaved} />}
      {previewForm && <FormPreviewModal form={previewForm} onClose={() => setPreviewForm(null)} />}
      {submissionsForm && <SubmissionsModal form={submissionsForm} onClose={() => setSubmissionsForm(null)} />}
      {sendModal && <div className={styles.modalOverlay} onClick={() => !sendLoading && setSendModal(null)}><div className={styles.sendModal} onClick={(event) => event.stopPropagation()}><div className={styles.sendHeader}><div><span>שליחה מרוכזת</span><h2>{sendModal.title}</h2></div><button onClick={() => setSendModal(null)}><Icon name="close" /></button></div><p>בחרו את קהל היעד. לכל לקוח יישלח קישור אישי ומאובטח.</p><div className={styles.sendOptions}><button disabled={sendLoading} onClick={() => handleBulkSend("all")}><Icon name="users" /><span><strong>כל הלקוחות</strong><small>שליחה לכל רשימת הלקוחות</small></span></button><button disabled={sendLoading} onClick={() => handleBulkSend("unsigned")}><Icon name="form" /><span><strong>לקוחות שטרם חתמו</strong><small>רק למי שאין הגשה קיימת</small></span></button><button disabled={sendLoading} onClick={() => handleBulkSend("signed")}><Icon name="copy" /><span><strong>חתימה מחדש</strong><small>שליחה ללקוחות שכבר חתמו בעבר</small></span></button></div>{sendLoading && <div className={styles.sending}>שולח קישורים...</div>}</div></div>}
    </Layout>
  );
};

export default Forms;
